if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.wireUpTrack();
      }

      // Pulled out of the constructor so a color-swap re-render (assets/product-gallery-dots.js,
      // setUpColorGallerySync) can rebind everything to the freshly swapped-in track/slides
      // without needing a whole new <product-modal> element — the base ModalDialog.
      // connectedCallback (assets/global.js) is a one-time `this.moved` guard that relocates the
      // element to a direct child of <body> on its first real connection, so a *second* instance
      // (what outerHTML replacement would create) finds no `.shopify-section` ancestor any more
      // once it's already living under <body> — confirmed live: swapping the whole element that
      // way threw `Cannot read properties of null (reading 'id')` from that exact
      // `this.closest('.shopify-section').id` line. Swapping just the content (innerHTML) and
      // re-wiring in place avoids ever re-running connectedCallback at all.
      //
      // That re-wiring has to include the close button: it's part of the same content being
      // replaced, so the *new* button element has no click listener of its own (the base
      // ModalDialog constructor only ever bound one, once, to the original button that's now
      // gone) — real bug caught live: close silently stopped working the moment a color swap had
      // swapped the modal in a session, but only noticeably on touch (a real click's own
      // `pointerup` still bubbles up and closes the modal via ModalDialog's own backdrop-click
      // handler regardless of which element it hit, papering over the missing listener on
      // desktop — but that handler only fires for `pointerType === 'mouse'`, so the same bug is
      // silent on touch).
      wireUpTrack() {
        this.wireUpCloseButton();
        this.setUpDragGestures();
        this.setUpTouchDismissAndTapGuard();
        this.setUpZoomResetOnScroll();
        this.setUpPinchZoom();
        this.setUpDoubleClickZoom();
        this.wireUpControls();
        this.preventNativeImageDrag();
      }

      // Desktop: a click-drag on a zoomed photo has to pan it (setUpDragGestures) — but browsers
      // start a native "drag the image" ghost from a mouse press on an <img> first, which swallowed
      // the pointer stream. Turning that off is what lets the pan work.
      preventNativeImageDrag() {
        this.querySelectorAll('img').forEach((img) => {
          img.setAttribute('draggable', 'false');
        });
        if (this._noDragBound) return;
        this._noDragBound = true;
        this.addEventListener('dragstart', (event) => event.preventDefault());
      }

      // ---- Lightbox chrome: counter, prev/next, zoom toggle, keyboard --------------------------
      // The controls live inside the dialog markup (snippets/product-media-modal.liquid), so a
      // color-swap re-render (which replaces the modal's innerHTML) brings fresh copies that need
      // binding again — hence this being part of wireUpTrack.
      slides() {
        return Array.from(this.querySelectorAll('.product-media-modal__slide'));
      }

      // Index of whichever slide is scrolled into view (nearest to the track's left edge).
      currentIndex() {
        const slides = this.slides();
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track || slides.length <= 1) return 0;
        const trackLeft = track.getBoundingClientRect().left;
        let best = 0;
        let bestDist = Infinity;
        slides.forEach((slide, i) => {
          const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        });
        return best;
      }

      goTo(index) {
        const slides = this.slides();
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track || !slides.length) return;
        const next = Math.min(slides.length - 1, Math.max(0, index));
        // Stepping away from a zoomed photo resets it, same as swiping away does.
        this.resetAllZoom();
        // The smooth scroll takes a moment, and its live position would make a second quick press
        // (arrow key held / double-tapped button) step from the *old* index — so the destination is
        // remembered until the scroll settles.
        this._pendingIndex = next;
        clearTimeout(this._pendingTimer);
        this._pendingTimer = setTimeout(() => {
          this._pendingIndex = null;
          this.updateChrome();
        }, 700);
        track.scrollTo({ left: slides[next].offsetLeft, behavior: 'smooth' });
        this.updateChrome(next);
      }

      // The index a next/previous step should start from: the pending destination while a smooth
      // scroll is in flight, otherwise whatever's on screen.
      stepIndex() {
        return typeof this._pendingIndex === 'number' ? this._pendingIndex : this.currentIndex();
      }

      // Counter text, prev/next disabled state, and which controls exist at all (a single photo
      // needs no counter or arrows).
      updateChrome(forceIndex) {
        const slides = this.slides();
        // Closed (the page-load call from wireUpControls, and any stray scroll event), nobody is
        // looking at the counter, and working out the current slide means a getBoundingClientRect
        // per slide — a forced layout while the page is still being built (~33ms in PageSpeed).
        // show() runs this again once the lightbox is open and the real index matters.
        const index =
          typeof forceIndex === 'number' ? forceIndex : this.hasAttribute('open') ? this.stepIndex() : 0;
        const multi = slides.length > 1;
        this.toggleAttribute('data-multi', multi);
        this.preloadNearby(index);
        const counter = this.querySelector('[data-modal-counter]');
        if (counter) counter.textContent = multi ? `${index + 1} / ${slides.length}` : '';
        const prev = this.querySelector('[data-modal-prev]');
        const next = this.querySelector('[data-modal-next]');
        if (prev) prev.disabled = index <= 0;
        if (next) next.disabled = index >= slides.length - 1;
        this.syncZoomButton();
      }

      syncZoomButton() {
        const button = this.querySelector('[data-modal-zoom-toggle]');
        if (!button) return;
        const wrap = this.activeZoomWrap();
        const zoomed = !!wrap && (wrap._zoomScale || 1) > 1;
        button.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
        button.setAttribute('aria-label', zoomed ? ((window.aiStrings && window.aiStrings.zoomOut) || 'Zoom out') : ((window.aiStrings && window.aiStrings.zoomIn) || 'Zoom in'));
      }

      // Zoom button / +/- keys: toggle between fit and 2.5x, centered on the middle of the screen.
      toggleZoom() {
        const wrap = this.activeZoomWrap();
        if (!wrap) return;
        if ((wrap._zoomScale || 1) > 1) {
          this.resetZoom(wrap);
          this.syncZoomButton();
        } else {
          this.zoomAt(wrap, 2.5, window.innerWidth / 2, window.innerHeight / 2);
        }
      }

      wireUpControls() {
        const prev = this.querySelector('[data-modal-prev]');
        const next = this.querySelector('[data-modal-next]');
        const zoom = this.querySelector('[data-modal-zoom-toggle]');
        prev?.addEventListener('click', () => this.goTo(this.stepIndex() - 1));
        next?.addEventListener('click', () => this.goTo(this.stepIndex() + 1));
        zoom?.addEventListener('click', () => this.toggleZoom());

        // ModalDialog closes on any mouse pointerup inside the modal that isn't on the media
        // itself (a backdrop click) — the chrome buttons must not count as backdrop.
        this.querySelectorAll('.product-media-modal__control, [id^="ModalClose-"], [data-modal-counter]').forEach(
          (el) => {
            el.addEventListener('pointerup', (event) => event.stopPropagation());
          }
        );

        // Counter/arrows follow the scroll as the shopper swipes or drags between photos.
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (track && !track._chromeBound) {
          track._chromeBound = true;
          let raf = null;
          track.addEventListener(
            'scroll',
            () => {
              if (raf) return;
              raf = requestAnimationFrame(() => {
                raf = null;
                this.updateChrome();
              });
            },
            { passive: true }
          );
        }
        this.updateChrome();
      }

      // Keyboard: ←/→ between photos, Home/End to the first/last, +/− zoom. (Escape already closes
      // via ModalDialog.) Bound only while open so it never steals keys from the page behind.
      handleKeydown = (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.metaKey || event.ctrlKey || event.altKey) return;
        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            this.goTo(this.stepIndex() + 1);
            break;
          case 'ArrowLeft':
            event.preventDefault();
            this.goTo(this.stepIndex() - 1);
            break;
          case 'Home':
            event.preventDefault();
            this.goTo(0);
            break;
          case 'End':
            event.preventDefault();
            this.goTo(this.slides().length - 1);
            break;
          case '+':
          case '=': {
            event.preventDefault();
            const wrap = this.activeZoomWrap();
            if (wrap && (wrap._zoomScale || 1) <= 1) this.toggleZoom();
            break;
          }
          case '-':
          case '_': {
            event.preventDefault();
            const wrap = this.activeZoomWrap();
            if (wrap && (wrap._zoomScale || 1) > 1) this.toggleZoom();
            break;
          }
        }
      };

      wireUpCloseButton() {
        const closeButton = this.querySelector('[id^="ModalClose-"]');
        closeButton?.addEventListener('click', () => this.hide());
      }

      hide() {
        // Cowalkies: the base ModalDialog.hide() (assets/global.js) restores focus to whatever
        // opened the modal — here, the intentionally invisible, zero-size `.product__media-
        // toggle` button (see assets/section-main-product.css) — via a plain `.focus()` with no
        // `preventScroll`. Focusing an off-screen/zero-size element makes the browser scroll the
        // page to reveal it, which is exactly the "closing the zoom jumps the page down to the
        // buy box" bug this fixes: save the page's own scroll position first and restore it
        // immediately after, canceling out that jump within the same tick, before any repaint.
        const scrollY = window.scrollY;
        super.hide();
        window.scrollTo(0, scrollY);
        this.resetAllZoom();
        this.resetDismissDrag();
        document.removeEventListener('keydown', this.handleKeydown);
      }

      show(opener) {
        super.show(opener);
        this.scrollToOpenedMedia();
        this.updateChrome();
        document.addEventListener('keydown', this.handleKeydown);
      }

      // The modal's photos are lazy by default and sit off-screen inside the horizontal track, so
      // the one you opened (a bigger rendition than the page's own gallery) would only start
      // downloading after it was already showing a loading placeholder. Kick off the opened photo
      // and its neighbours right away — with a visible progress ring for any that aren't cached.
      preloadNearby(index) {
        // Only while actually open — updateChrome also runs at page load, and the viewer's big
        // renditions shouldn't download before anyone asked to see them.
        if (!this.hasAttribute('open')) return;
        const slides = this.slides();
        [index - 1, index, index + 1].forEach((i) => {
          const img = slides[i]?.querySelector('img');
          if (img) this.loadWithProgress(img);
        });
      }

      // Picks the srcset candidate the browser would (roughly): the smallest one at least as wide
      // as the screen in device pixels, else the largest.
      pickSource(img) {
        const srcset = img.getAttribute('srcset');
        if (!srcset) return img.currentSrc || img.src || null;
        const candidates = srcset
          .split(',')
          .map((entry) => entry.trim().split(/\s+/))
          .filter((parts) => parts[0] && /w$/.test(parts[1] || ''))
          .map((parts) => ({ url: parts[0], width: parseInt(parts[1], 10) }))
          .sort((a, b) => a.width - b.width);
        if (!candidates.length) return img.currentSrc || img.src || null;
        const target = Math.min(window.innerWidth * (window.devicePixelRatio || 1), 2048);
        return (candidates.find((c) => c.width >= target) || candidates[candidates.length - 1]).url;
      }

      // Downloads the photo via fetch so the real byte progress can drive a ring + percentage
      // (a plain <img> gives no progress at all), then hands the finished bytes to the <img> as a
      // blob URL. Anything already in the browser cache is skipped entirely (no ring flash), and
      // any failure — no streaming support, a missing Content-Length (ring spins without a
      // number), a network error — falls back to letting the <img> load itself normally.
      loadWithProgress(img) {
        if (img._cwProgress) return;
        if (img.complete && img.naturalWidth > 0) return;
        img._cwProgress = true;

        const slide = img.closest('.product-media-modal__slide');
        const url = this.pickSource(img);
        const fallback = () => {
          img.loading = 'eager';
          this.removeLoader(slide);
        };
        if (!slide || !url || !window.fetch || !window.ReadableStream) return fallback();

        const loader = this.makeLoader(slide);
        img.addEventListener('load', () => this.removeLoader(slide), { once: true });
        img.addEventListener('error', () => this.removeLoader(slide), { once: true });

        fetch(url)
          .then((response) => {
            if (!response.ok || !response.body) throw new Error('image fetch failed');
            const total = parseInt(response.headers.get('content-length'), 10) || 0;
            const type = response.headers.get('content-type') || 'image/jpeg';
            const reader = response.body.getReader();
            const chunks = [];
            let received = 0;
            const pump = () =>
              reader.read().then(({ done, value }) => {
                if (done) return new Blob(chunks, { type });
                chunks.push(value);
                received += value.length;
                this.setLoaderProgress(loader, total ? received / total : null);
                return pump();
              });
            return pump();
          })
          .then((blob) => {
            this.setLoaderProgress(loader, 1);
            const objectUrl = URL.createObjectURL(blob);
            img.addEventListener('load', () => URL.revokeObjectURL(objectUrl), { once: true });
            // The blob URL hides which file this actually is; keep the real one findable.
            img.dataset.cwSource = url;
            img.removeAttribute('srcset');
            img.loading = 'eager';
            img.src = objectUrl;
          })
          .catch(fallback);
      }

      makeLoader(slide) {
        let loader = slide.querySelector('.product-media-modal__loader');
        if (loader) return loader;
        loader = document.createElement('div');
        loader.className = 'product-media-modal__loader';
        loader.setAttribute('role', 'progressbar');
        loader.setAttribute('aria-label', (window.aiStrings && window.aiStrings.loadingPhoto) || 'Loading photo');
        loader.innerHTML =
          '<svg viewBox="0 0 64 64" width="64" height="64" aria-hidden="true">' +
          '<circle class="product-media-modal__loader-track" cx="32" cy="32" r="26" fill="none" stroke-width="3"/>' +
          '<circle class="product-media-modal__loader-ring" cx="32" cy="32" r="26" fill="none" stroke-width="3" stroke-linecap="round" pathLength="100" stroke-dasharray="100" stroke-dashoffset="100"/>' +
          '</svg><span class="product-media-modal__loader-pct"></span>';
        slide.appendChild(loader);
        loader.classList.add('is-indeterminate');
        return loader;
      }

      setLoaderProgress(loader, fraction) {
        if (!loader || !loader.isConnected) return;
        const ring = loader.querySelector('.product-media-modal__loader-ring');
        const label = loader.querySelector('.product-media-modal__loader-pct');
        if (fraction == null) {
          // No Content-Length to measure against: keep the spinning ring, no number.
          loader.classList.add('is-indeterminate');
          return;
        }
        const pct = Math.min(100, Math.max(0, Math.round(fraction * 100)));
        loader.classList.remove('is-indeterminate');
        ring.setAttribute('stroke-dashoffset', String(100 - pct));
        label.textContent = `${pct}%`;
        loader.setAttribute('aria-valuenow', String(pct));
      }

      removeLoader(slide) {
        const loader = slide?.querySelector('.product-media-modal__loader');
        if (!loader) return;
        loader.classList.add('is-done');
        setTimeout(() => loader.remove(), 250);
      }


      // Cowalkies: every slide sits in a normal scroll-snap track (see the "Zoom modal" comment
      // in sections/main-product.liquid) — every `.product-media-modal__zoom-wrap` is laid out
      // simultaneously, so "the active one" just means whichever slide is currently scrolled
      // into view. A plain nearest-scrollLeft search, not Dawn's own SliderComponent tracking —
      // that needs a prev/next button to even activate (see assets/global.js), and this modal
      // has neither any more (removed after a first pass — scrolling is the only way to move
      // between photos now).
      activeZoomWrap() {
        const wraps = this.querySelectorAll('.product-media-modal__zoom-wrap');
        if (wraps.length <= 1) return wraps[0] || null;
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track) return wraps[0] || null;
        const trackLeft = track.getBoundingClientRect().left;
        let closest = null;
        let closestDist = Infinity;
        wraps.forEach((wrap) => {
          const slide = wrap.closest('li');
          if (!slide) return;
          const dist = Math.abs(slide.getBoundingClientRect().left - trackLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closest = wrap;
          }
        });
        return closest || wraps[0];
      }

      scrollToOpenedMedia() {
        const track = this.querySelector('[id^="Slider-Modal-"]');
        const targetId = this.openedBy?.getAttribute('data-media-id');
        if (!track || !targetId) return;
        const match = this.querySelector(`[data-media-id="${targetId}"]`);
        const slide = match?.closest('li');
        if (slide) track.scrollLeft = slide.offsetLeft;
      }

      // Resets a photo's pinch-zoom once the shopper has scrolled/swiped away from it — not
      // during a still-in-progress scroll (which would fight a drag mid-gesture), just once it
      // settles on a new slide.
      setUpZoomResetOnScroll() {
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track) return;
        let scrollEndTimer;
        track.addEventListener(
          'scroll',
          () => {
            clearTimeout(scrollEndTimer);
            scrollEndTimer = setTimeout(() => {
              const active = this.activeZoomWrap();
              this.querySelectorAll('.product-media-modal__zoom-wrap').forEach((wrap) => {
                if (wrap !== active) this.resetZoom(wrap);
              });
            }, 150);
          },
          { passive: true }
        );
      }

      // One combined gesture handler for the track, covering everything a pointer-down there can
      // mean: panning a zoomed-in photo, scrolling horizontally between photos, swiping down to
      // dismiss, or a plain tap. Mouse only (real touch has its own handler below, since a phone
      // needs genuinely different rules — e.g. one-finger touch already scrolls the track
      // natively and shouldn't be fought, but two-finger touch needs its own pan+pinch handling).
      //
      // This modal is a `media-modal` (see ModalDialog in assets/global.js), which closes on any
      // mouse `pointerup` inside it that isn't on a deferred-media/product-model element — this
      // handler always stops that propagation and decides *itself* whether to close, since
      // Dawn's own version can't tell a tap on the photo (shouldn't close it — it's interactive:
      // zoomable, pannable) from a tap on the backdrop around it (should).
      setUpDragGestures() {
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track) return;

        let pointerId = null;
        let startX = 0;
        let startY = 0;
        let lastX = 0;
        let lastY = 0;
        let mode = null; // 'pan' | 'horizontal' | 'vertical', decided once past the hysteresis distance
        let startScrollLeft = 0;
        let startedOnMedia = false;

        const isOnMedia = (target) => !!target.closest('.product-media-modal__zoom-wrap img, .product-media-modal__zoom-wrap .media');

        track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse') return;
          pointerId = event.pointerId;
          startX = lastX = event.clientX;
          startY = lastY = event.clientY;
          mode = null;
          startScrollLeft = track.scrollLeft;
          startedOnMedia = isOnMedia(event.target);
        });

        track.addEventListener('pointermove', (event) => {
          if (event.pointerId !== pointerId) return;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          const stepX = event.clientX - lastX;
          const stepY = event.clientY - lastY;
          lastX = event.clientX;
          lastY = event.clientY;

          if (mode === null) {
            const wrap = this.activeZoomWrap();
            const zoomed = wrap && (wrap._zoomScale || 1) > 1;
            // 10px — the same hysteresis distance apple-design's gesture-detail rules call for
            // before committing a pointer-down to a drag rather than a tap.
            if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
            if (zoomed) mode = 'pan';
            else mode = Math.abs(dy) > Math.abs(dx) ? 'vertical' : 'horizontal';
            track.classList.toggle('is-dragging', mode === 'horizontal');
          }

          if (mode === 'pan') {
            const wrap = this.activeZoomWrap();
            if (wrap) this.panBy(wrap, stepX, stepY);
          } else if (mode === 'horizontal') {
            track.scrollLeft = startScrollLeft - dx;
          } else if (mode === 'vertical') {
            this.applyDismissDrag(dy);
          }
        });

        const endDrag = (event) => {
          if (event.pointerId !== pointerId) return;
          pointerId = null;
          track.classList.remove('is-dragging');

          if (mode === 'vertical') {
            // 100px — far enough past the hysteresis distance above that it reads as a
            // deliberate dismiss swipe, not just a slightly-diagonal horizontal one. Either
            // direction dismisses — swipe up or down, same as the touch version below.
            if (Math.abs(lastY - startY) > 100) this.hide();
            else this.resetDismissDrag();
          } else if (mode === null && !startedOnMedia) {
            // A plain tap (no drag committed) that didn't start on the photo itself — the
            // backdrop/letterboxed space around it — closes, same as tapping outside any other
            // lightbox. A tap that *did* start on the photo does nothing (the photo is
            // interactive, not a close target).
            this.hide();
          }

          mode = null;
          event.stopPropagation();
        };
        track.addEventListener('pointerup', endDrag);
        track.addEventListener('pointercancel', endDrag);
      }

      // Touch's own version of the tap-to-close-on-backdrop + swipe-down-to-dismiss behavior in
      // setUpDragGestures above — kept separate rather than folded into that one handler because
      // touch genuinely needs different rules: the track's own `touch-action: pan-x` (see
      // sections/main-product.liquid) already leaves *horizontal* one-finger movement to the
      // browser's native scroll-snap swipe entirely untouched, so this only ever has to step in
      // for a predominantly *vertical* one-finger drag (which `pan-x` already excludes from
      // native handling) or a plain tap. Two-finger touch (pinch/pan-when-zoomed) is its own
      // thing, handled in setUpPinchZoom.
      setUpTouchDismissAndTapGuard() {
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track) return;

        const isOnMedia = (target) => !!target.closest('.product-media-modal__zoom-wrap img, .product-media-modal__zoom-wrap .media');

        let active = false;
        let startX = 0;
        let startY = 0;
        let lastX = 0;
        let lastY = 0;
        let verticalDrag = false;
        let panning = false;
        let startedOnMedia = false;

        track.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) {
              active = false;
              return;
            }
            active = true;
            verticalDrag = false;
            panning = false;
            startX = lastX = event.touches[0].clientX;
            startY = lastY = event.touches[0].clientY;
            startedOnMedia = isOnMedia(event.target);
          },
          { passive: true }
        );

        track.addEventListener(
          'touchmove',
          (event) => {
            if (!active || event.touches.length !== 1) return;
            const x = event.touches[0].clientX;
            const y = event.touches[0].clientY;
            const dx = x - startX;
            const dy = y - startY;

            if (!verticalDrag && !panning) {
              const wrap = this.activeZoomWrap();
              if (wrap && (wrap._zoomScale || 1) > 1) {
                // Zoomed in — a one-finger drag pans around the photo (the same gesture as the
                // mouse click-drag pan and real two-finger touch pan in setUpPinchZoom) instead
                // of the dismiss-swipe/tap-to-close handling below, which only makes sense at 1x.
                panning = true;
              } else {
                // 10px hysteresis, same as the mouse gesture handler — and only commit to owning
                // this gesture once it's clearly *more* vertical than horizontal, so a real
                // swipe-between-photos never gets hijacked.
                if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
                if (Math.abs(dy) <= Math.abs(dx)) {
                  active = false; // horizontal — leave it to the native pan-x scroll entirely
                  return;
                }
                verticalDrag = true;
              }
            }

            event.preventDefault();
            if (panning) {
              const wrap = this.activeZoomWrap();
              if (wrap) this.panBy(wrap, x - lastX, y - lastY);
            } else {
              this.applyDismissDrag(dy);
            }
            lastX = x;
            lastY = y;
          },
          { passive: false }
        );

        const endTouch = () => {
          if (!active) return;
          if (verticalDrag) {
            // 100px — matches the mouse gesture's own dismiss threshold. Either direction
            // dismisses — swipe up or down.
            if (Math.abs(lastY - startY) > 100) this.hide();
            else this.resetDismissDrag();
          } else if (!verticalDrag && !panning && !startedOnMedia && lastY === startY) {
            // A plain tap (touchmove never fired at all) that didn't start on the photo itself.
            this.hide();
          }
          active = false;
          verticalDrag = false;
          panning = false;
        };
        track.addEventListener('touchend', endTouch);
        track.addEventListener('touchcancel', endTouch);
      }

      // Live 1:1 drag feedback while swiping to dismiss (apple-design's "direct manipulation" —
      // the photo has to visibly follow the finger the whole way, not just animate once the
      // gesture ends) — translates and fades the active photo, and dims the backdrop's own
      // opacity by the same fraction, so the two read as one continuous motion. Either direction
      // works — up or down — so `distance`/the translate both use the signed delta, not a
      // downward-only clamp.
      applyDismissDrag(dy) {
        const distance = Math.abs(dy);
        const wrap = this.activeZoomWrap();
        if (!wrap) return;
        wrap.style.transition = 'none';
        wrap.style.transform = `translateY(${dy}px)`;
        const fade = Math.max(0.4, 1 - distance / 400);
        wrap.style.opacity = String(fade);
        this.style.setProperty('--ai-pdp-modal-drag-fade', String(fade));

        // Backdrop itself: real bug reported live — the blur stayed at full strength for the
        // *entire* drag and only cleared once the finger lifted, instead of clearing out as the
        // shopper actually swipes, so releasing past the dismiss threshold read as an abrupt
        // "was blurred, now instantly isn't" rather than a continuous motion. 100px matches the
        // release threshold in setUpDragGestures/setUpTouchDismissAndTapGuard below, so the
        // backdrop is already fully clear right at the point where letting go would dismiss.
        // Set directly (inline, `transition: none`) rather than through the CSS `[open]` rule's
        // own `backdrop-filter` transition — same reasoning as the photo's transform/opacity
        // above, this has to track the finger 1:1 with zero lag.
        const clearProgress = Math.min(1, distance / 100);
        const blur = 20 * (1 - clearProgress);
        const saturate = 160 - 60 * clearProgress;
        this.style.transition = 'none';
        this.style.backdropFilter = `blur(${blur}px) saturate(${saturate}%)`;
        this.style.webkitBackdropFilter = this.style.backdropFilter;
      }

      resetDismissDrag() {
        const wrap = this.activeZoomWrap();
        if (wrap) {
          wrap.style.transition = '';
          wrap.style.transform = '';
          wrap.style.opacity = '';
        }
        this.style.removeProperty('--ai-pdp-modal-drag-fade');
        // Clears the inline overrides above, handing the backdrop back to the CSS `[open]`
        // rule's own value/transition — animates smoothly back to full blur on a cancelled
        // (spring-back) drag, since the modal is staying open rather than closing.
        this.style.transition = '';
        this.style.backdropFilter = '';
        this.style.webkitBackdropFilter = '';
      }

      // Pans a zoomed-in photo — mouse click-drag (setUpDragGestures above) and real two-finger
      // touch drag (setUpPinchZoom below) both call this with the same per-frame pixel delta, so
      // panning feels identical regardless of input device.
      panBy(wrap, dx, dy) {
        wrap._panX = (wrap._panX || 0) + dx;
        wrap._panY = (wrap._panY || 0) + dy;
        this.clampPan(wrap);
        this.renderZoomTransform(wrap);
      }

      // Keeps a zoomed photo from panning so far that its edge leaves a gap — clamps to roughly
      // half of however much larger than its own box the current scale makes it (the other half
      // stays pinned to center either way, so this doesn't need to be pixel-exact).
      clampPan(wrap) {
        const scale = wrap._zoomScale || 1;
        if (scale <= 1) {
          wrap._panX = 0;
          wrap._panY = 0;
          return;
        }
        const rect = wrap.getBoundingClientRect();
        const baseWidth = rect.width / scale;
        const baseHeight = rect.height / scale;
        const maxX = (baseWidth * (scale - 1)) / 2;
        const maxY = (baseHeight * (scale - 1)) / 2;
        wrap._panX = Math.min(maxX, Math.max(-maxX, wrap._panX || 0));
        wrap._panY = Math.min(maxY, Math.max(-maxY, wrap._panY || 0));
      }

      renderZoomTransform(wrap) {
        const scale = wrap._zoomScale || 1;
        const panX = wrap._panX || 0;
        const panY = wrap._panY || 0;
        wrap.style.transform = scale > 1 ? `translate(${panX}px, ${panY}px) scale(${scale})` : '';
        wrap.classList.toggle('is-zoomed', scale > 1);
        this.syncZoomButton();
      }

      clampZoomScale(value) {
        return Math.min(4, Math.max(1, value));
      }

      markZooming(wrap) {
        wrap.classList.add('is-zooming');
        clearTimeout(wrap._zoomIdleTimer);
        wrap._zoomIdleTimer = setTimeout(() => wrap.classList.remove('is-zooming'), 300);
      }

      // Keeps the point under the cursor/pinch-midpoint/double-click visually fixed as scale
      // changes — standard incremental zoom-to-point: the new pan is whatever keeps
      // `origin - pan` (the point's offset from the photo's own center) constant relative to
      // the *scaled* photo. Shared by setUpPinchZoom (wheel + touch pinch) and
      // setUpDoubleClickZoom below, so every way of zooming in centers on the same spot.
      zoomAt(wrap, nextScale, clientX, clientY) {
        const rect = wrap.getBoundingClientRect();
        const prevScale = wrap._zoomScale || 1;
        const originX = clientX - rect.left - rect.width / 2;
        const originY = clientY - rect.top - rect.height / 2;
        const ratio = nextScale / prevScale;
        wrap._panX = originX - (originX - (wrap._panX || 0)) * ratio;
        wrap._panY = originY - (originY - (wrap._panY || 0)) * ratio;
        wrap._zoomScale = nextScale;
        this.clampPan(wrap);
        this.renderZoomTransform(wrap);
        this.markZooming(wrap);
      }

      // Pinch-to-zoom (+ pan once zoomed) on whichever photo is currently in view — trackpad
      // pinch (and ctrl+scroll, which browsers report identically) via `wheel`, real touchscreen
      // pinch/pan via two-finger `touchmove` (one-finger touch is left alone — that's the
      // track's own native swipe-between-photos scroll, except once already zoomed in, where
      // setUpTouchDismissAndTapGuard's own one-finger handler takes over panning). Scales/pans
      // the zoom-wrap, not the image itself.
      setUpPinchZoom() {
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track) return;

        track.addEventListener(
          'wheel',
          (event) => {
            const wrap = this.activeZoomWrap();
            if (event.ctrlKey) {
              // A trackpad pinch gesture, or a real ctrl+scroll — browsers report both
              // identically as a ctrl-flagged wheel event.
              event.preventDefault();
              if (!wrap) return;
              const nextScale = this.clampZoomScale((wrap._zoomScale || 1) - event.deltaY * 0.01);
              this.zoomAt(wrap, nextScale, event.clientX, event.clientY);
              return;
            }
            // A plain two-finger trackpad move (no pinch) once already zoomed in — "just move it
            // with two fingers" on a Mac trackpad. Left alone at 1x so it keeps scrolling
            // between photos the normal way.
            if (wrap && (wrap._zoomScale || 1) > 1) {
              event.preventDefault();
              this.panBy(wrap, -event.deltaX, -event.deltaY);
            }
          },
          { passive: false }
        );

        let pinchStartDist = null;
        let pinchStartScale = 1;
        let pinchLastMidX = 0;
        let pinchLastMidY = 0;

        track.addEventListener(
          'touchmove',
          (event) => {
            if (event.touches.length !== 2) return;
            event.preventDefault();
            const wrap = this.activeZoomWrap();
            if (!wrap) return;
            const [t1, t2] = event.touches;
            const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
            const midX = (t1.clientX + t2.clientX) / 2;
            const midY = (t1.clientY + t2.clientY) / 2;
            if (pinchStartDist == null) {
              pinchStartDist = dist;
              pinchStartScale = wrap._zoomScale || 1;
              pinchLastMidX = midX;
              pinchLastMidY = midY;
            }
            const nextScale = this.clampZoomScale(pinchStartScale * (dist / pinchStartDist));
            this.zoomAt(wrap, nextScale, midX, midY);
            // The pinch midpoint moving (fingers drifting together, not just apart/closer) pans
            // the photo the same way a plain two-finger drag would once already zoomed in.
            this.panBy(wrap, midX - pinchLastMidX, midY - pinchLastMidY);
            pinchLastMidX = midX;
            pinchLastMidY = midY;
          },
          { passive: false }
        );

        const endPinch = () => {
          pinchStartDist = null;
        };
        track.addEventListener('touchend', endPinch);
        track.addEventListener('touchcancel', endPinch);
      }

      // Double-click (mouse) toggles zoom on whichever photo is currently in view, centered on
      // the click point — the discoverable, no-instructions-needed way to start a mouse zoom,
      // since ctrl+scroll (setUpPinchZoom above) isn't something most shoppers would think to
      // try. Once zoomed in, setUpDragGestures' own click-and-drag takes over panning; a second
      // double-click zooms back out.
      setUpDoubleClickZoom() {
        const track = this.querySelector('[id^="Slider-Modal-"]');
        if (!track) return;

        const isOnMedia = (target) => !!target.closest('.product-media-modal__zoom-wrap img, .product-media-modal__zoom-wrap .media');

        track.addEventListener('dblclick', (event) => {
          if (!isOnMedia(event.target)) return;
          event.preventDefault();
          const wrap = this.activeZoomWrap();
          if (!wrap) return;
          if ((wrap._zoomScale || 1) > 1) {
            this.resetZoom(wrap);
          } else {
            this.zoomAt(wrap, 2.5, event.clientX, event.clientY);
          }
        });
      }

      resetZoom(wrap) {
        wrap._zoomScale = 1;
        wrap._panX = 0;
        wrap._panY = 0;
        wrap.style.transform = '';
        wrap.classList.remove('is-zoomed', 'is-zooming');
        this.syncZoomButton();
      }

      resetAllZoom() {
        this.querySelectorAll('.product-media-modal__zoom-wrap').forEach((wrap) => this.resetZoom(wrap));
      }
    }
  );
}
